// 用户管理页(admin) — Fluent UI v9 单列列表风格
// 点击行进入用户详情页(操作按钮移至详情页)
// 消息反馈:顶部 Toast
// oauth_only 模式:本地账号由 OAuth 提供方管理,本页面整体禁用(保留数据但隐藏入口)。
// 分页固定在底部(与 浏览/我的仓库 一致):root height:100% + body(flex:1,滚动) + pagination(flexShrink:0)
import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Input,
  Button,
  Avatar,
  Badge,
  Title3,
  Label,
  Text,
  Body1,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import {
  AddRegular,
  SearchRegular,
  ShieldCheckmarkRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, type CreateUserReq } from '@/api/admin';
import { explainError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { SimpleList, type SimpleListItem } from '@/components/SimpleList';
import { HelpPopover } from '@/components/HelpPopover';
import { DialogCloseTitle } from '@/components/DialogCloseTitle';
import { toast } from '@/lib/toast';
import { getAuthMode } from '@/lib/authMode';
import { UserDetailDialog } from '@/pages/UserDetailPage';

const STATUS_META: Record<string, { color: 'success' | 'subtle' | 'warning'; label: string }> = {
  active: { color: 'success', label: '正常' },
  suspended: { color: 'subtle', label: '已停用' },
  pending: { color: 'warning', label: '待激活' },
};

const PAGE_SIZE = 20;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: tokens.spacingVerticalXL,
  },
  spacer: { flex: 1 },
  // 列表区 flex:1 独占剩余高度并滚动,分页才贴底
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: tokens.spacingVerticalL,
  },
  pagination: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  fieldError: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorStatusDangerForeground3,
  },
});

export function UserManagementPage() {
  const styles = useStyles();
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [pageIndex, setPageIndex] = useState(1);

  // 用户详情对话框:null 表示关闭,选中用户名即打开
  const [detailUsername, setDetailUsername] = useState<string | null>(null);

  // 新建用户弹窗
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fUsername, setFUsername] = useState('');
  const [fEmail, setFEmail] = useState('');
  const [fPassword, setFPassword] = useState('');
  const [fDisplayName, setFDisplayName] = useState('');
  const [fErrors, setFErrors] = useState<{ username?: string; email?: string; password?: string }>({});

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers(100, 0),
    enabled: !!me?.is_admin,
  });

  const users = data?.users ?? [];

  const filtered = useMemo(() => {
    if (!keyword.trim()) return users;
    const q = keyword.trim().toLowerCase();
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.display_name ?? '').toLowerCase().includes(q),
    );
  }, [users, keyword]);

  // 关键字变化回到第一页
  useEffect(() => {
    setPageIndex(1);
  }, [keyword]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // 客户端分页:只把当前页传给 SimpleList
  const paged = useMemo(
    () => filtered.slice((pageIndex - 1) * PAGE_SIZE, (pageIndex - 1) * PAGE_SIZE + PAGE_SIZE),
    [filtered, pageIndex],
  );

  const items: SimpleListItem[] = useMemo(
    () =>
      paged.map((u) => {
        const statusMeta = STATUS_META[u.status] ?? STATUS_META.active;
        return {
          key: u.username,
          media: (
            <Avatar
              size={36}
              name={u.username}
              image={u.avatar_url ? { src: u.avatar_url } : undefined}
              color="brand"
            />
          ),
          title: (
            <>
              <span>{u.username}</span>
              {u.is_admin && (
                <Badge appearance="filled" color="warning">
                  管理员
                </Badge>
              )}
            </>
          ),
          description: `${u.email}${u.display_name ? ' · ' + u.display_name : ''}`,
          extra: (
            <Badge appearance="tint" color={statusMeta.color}>
              {statusMeta.label}
            </Badge>
          ),
          onClick: () => setDetailUsername(u.username),
        };
      }),
    [paged],
  );

  function resetForm() {
    setFUsername('');
    setFEmail('');
    setFPassword('');
    setFDisplayName('');
    setFErrors({});
  }

  function openModal() {
    resetForm();
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    resetForm();
  }

  async function doCreate() {
    const errs: { username?: string; email?: string; password?: string } = {};
    if (!fUsername) errs.username = '请输入用户名';
    else if (fUsername.length < 3 || fUsername.length > 32)
      errs.username = '用户名长度需为 3-32 个字符';
    if (!fEmail) errs.email = '请输入邮箱';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fEmail)) errs.email = '邮箱格式不正确';
    if (!fPassword) errs.password = '请设置初始密码';
    else if (fPassword.length < 8) errs.password = '密码至少 8 位';
    setFErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setCreating(true);
    try {
      const payload: CreateUserReq = {
        username: fUsername.trim(),
        email: fEmail.trim(),
        password: fPassword,
        display_name: fDisplayName.trim() || null,
      };
      const user = await adminApi.createUser(payload);
      toast.success(`用户 ${user.username} 创建成功`);
      setModalOpen(false);
      resetForm();
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className={styles.root}>
      <Title3 as="h1">用户管理</Title3>

      {/* oauth_only 模式:本地账号由 IdP 管理,这里只展示只读信息,
          所有写操作(创建/重置密码/启停/删除)已隐藏。
          数据库里的本地账号仍然存在,只是 UI 不再开放入口。 */}
      {getAuthMode() === 'oauth_only' ? (
        <>
          <MessageBar intent="warning" icon={<ShieldCheckmarkRegular />}>
            <MessageBarBody>
              当前仅支持 OAuth 2.0 登录,本地账号已禁用创建/修改。
              如需新增用户,请让其通过 OAuth 登录后由系统自动创建。
            </MessageBarBody>
          </MessageBar>
          <div
            style={{
              padding: tokens.spacingVerticalXL,
              backgroundColor: tokens.colorNeutralBackground2,
              borderRadius: tokens.borderRadiusLarge,
              textAlign: 'center',
            }}
          >
            <ShieldCheckmarkRegular
              fontSize={32}
              style={{ color: tokens.colorNeutralForeground3 }}
            />
            <Body1 block style={{ marginTop: tokens.spacingVerticalS }}>
              本地账号管理入口已在当前环境关闭
            </Body1>
            <Text block style={{ color: tokens.colorNeutralForeground3 }}>
              数据库中保留只读账号数据,可在「系统设置 → OAuth」中查看已启用的身份提供方。
            </Text>
          </div>
        </>
      ) : (
        <>
          <div className={styles.toolbar}>
            <Input
              placeholder="搜索用户名、邮箱或昵称"
              contentBefore={<SearchRegular />}
              value={keyword}
              onChange={(_, d) => setKeyword(d.value)}
              style={{ width: 280 }}
            />
            <HelpPopover content="按用户名、邮箱或昵称模糊搜索,留空则显示全部用户。" />
            <div className={styles.spacer} />
            <Button appearance="primary" icon={<AddRegular />} onClick={openModal}>
              新建用户
            </Button>
          </div>

      {/* 列表区(单独滚动,分页贴底) */}
      <div className={styles.body}>
        <SimpleList
          items={items}
          loading={isLoading}
          emptyText={keyword.trim() ? '没有匹配的用户' : '暂无用户'}
          pageSize={Number.POSITIVE_INFINITY}
        />
      </div>

      {/* 分页固定底部 */}
      {total > 0 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={pageIndex <= 1}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          />
          <Text className={styles.pageInfo}>
            第 {pageIndex} / {totalPages} 页 · 共 {total} 个
          </Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={pageIndex >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {/* 新建用户 */}
      <Dialog open={modalOpen} onOpenChange={(_, d) => setModalOpen(d.open)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="新建用户" onClose={closeModal} />
            <DialogContent>
              <div className={styles.field}>
                <Label className={styles.fieldLabel} htmlFor="cu-username">
                  用户名 <HelpPopover content="登录用的唯一标识,3–32 个字符,创建后不可修改。" />
                </Label>
                <Input
                  id="cu-username"
                  placeholder="3-32 个字符"
                  value={fUsername}
                  onChange={(_, d) => {
                    setFUsername(d.value);
                    setFErrors((p) => ({ ...p, username: undefined }));
                  }}
                />
                {fErrors.username && (
                  <Text className={styles.fieldError}>{fErrors.username}</Text>
                )}
              </div>
              <div className={styles.field}>
                <Label className={styles.fieldLabel} htmlFor="cu-email">
                  邮箱 <HelpPopover content="用户邮箱,用于通知与找回密码,需为有效邮箱格式。" />
                </Label>
                <Input
                  id="cu-email"
                  placeholder="user@example.com"
                  value={fEmail}
                  onChange={(_, d) => {
                    setFEmail(d.value);
                    setFErrors((p) => ({ ...p, email: undefined }));
                  }}
                />
                {fErrors.email && (
                  <Text className={styles.fieldError}>{fErrors.email}</Text>
                )}
              </div>
              <div className={styles.field}>
                <Label className={styles.fieldLabel} htmlFor="cu-password">
                  初始密码 <HelpPopover content="为该用户设置的初始登录密码,至少 8 位。建议创建后提醒用户尽快修改。" />
                </Label>
                <Input
                  id="cu-password"
                  type="password"
                  placeholder="至少 8 位"
                  value={fPassword}
                  onChange={(_, d) => {
                    setFPassword(d.value);
                    setFErrors((p) => ({ ...p, password: undefined }));
                  }}
                />
                {fErrors.password && (
                  <Text className={styles.fieldError}>{fErrors.password}</Text>
                )}
              </div>
              <div className={styles.field}>
                <Label className={styles.fieldLabel} htmlFor="cu-display">
                  昵称（可选） <HelpPopover content="展示名称,可留空。留空时默认使用用户名。" />
                </Label>
                <Input
                  id="cu-display"
                  placeholder="显示名称"
                  value={fDisplayName}
                  onChange={(_, d) => setFDisplayName(d.value)}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" disabled={creating} onClick={doCreate}>
                {creating ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {/* 用户详情对话框 */}
      <UserDetailDialog
        open={detailUsername !== null}
        username={detailUsername}
        onClose={() => setDetailUsername(null)}
      />
        </>
      )}
    </div>
  );
}
